import { useEffect, useState, useCallback, useMemo, use } from 'react'
import { getMyProfile, updateMyProfile, getCountries } from '@/api/kuizy'
import { getSocialGoogle, authGoogle, disconnectGoogle } from '@/api/auth'
import { API_BASE } from '@/settings/master'
import { motion } from 'framer-motion'
import Select from 'react-select'
import { Loader, Save, ImagePlus, Flag, Phone, Briefcase, User2, AlignLeft, AtSign, Link, Check } from 'lucide-react'
import { useGoogleLogin } from '@react-oauth/google'
import ConfirmModal from "@/widgets/ConfirmModal"
export default function UserProfilesPage() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [countries, setCountries] = useState([])
  const [form, setForm] = useState({
    first_name: '', last_name: '', display_name: '', job_title: '',
    phone_code: '', phone: '', country: '', about_me: '', avatar_url: '', avatar_path: '', email: ''
  })
  const [file, setFile] = useState(null)
  const [connectedGoogle, setConnectGoogle] = useState(false)
  const [connectSocial, setConnectSocial] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [delItem, setDelItem] = useState(null)
  const [alert, setAlert] = useState({ open: false, msg: '' })
  const init = useCallback(async () => {
    try {
      const [me, ctry] = await Promise.all([getMyProfile(), getCountries()])
      const d = me.data || {}
      setForm(f => ({ ...f, ...d }))
      setCountries(ctry.data || [])
    } catch { setErr('โหลดข้อมูลไม่สำเร็จ') } finally { setLoading(false) }
  }, [])

  const fetchSocials = async () => {
    try {
      const res = await getSocialGoogle()

      if (res?.data?.connected) {
        setConnectGoogle(true)
        setConnectSocial(res.data)
      } else {
        setConnectGoogle(false)
        setConnectSocial(null)
      }
    } catch (err) {
      console.error(err)
      setConnectGoogle(false)
      setConnectSocial(null)
    }
  }
  useEffect(() => {
    fetchSocials()
  }, [])

  const connectGoogle = useGoogleLogin({
    scope: 'openid email profile',
    onSuccess: (res) => {
      authGoogle(res.access_token) // ✅ access_token
      // fetchSocials() 
      setAlert({ open: true, msg: "เชื่อมต่อ Google สำเร็จ" })
    },
    onError: () => alert('Google login failed')
  })

  const handleDisconnectGoogle = async () => {
    setDelItem(true)
  }

  const confirmDelete = async () => {
    if (!delItem) return
    setDeleting(true)
    try {
      const res = await disconnectGoogle()
      if (!res.data.success) return setAlert({ open: true, msg: "ไม่สามารถยกเลิกการเชื่อมต่อได้" })
      fetchSocials()
      setAlert({ open: true, msg: "ยกเลิกการเชื่อมต่อ Google สำเร็จ" })
    } catch {
      setAlert({ open: true, msg: "ไม่สามารถลบได้" })
    } finally {
      fetchSocials()
      setDeleting(false)
      setDelItem(null)
    }
  }

  useEffect(() => { init() }, [init])

  const optCountries = useMemo(() => [
    { value: '', label: '—' },
    ...countries.map(c => ({ value: c.iso2, label: `${c.name_en} (${c.phone_code})`, phone_code: c.phone_code }))
  ], [countries])

  const handle = (k, v) => setForm(s => ({ ...s, [k]: v }))
  const onCountryChange = o => {
    const code = o?.phone_code || countries.find(c => c.iso2 === o?.value)?.phone_code || ''
    handle('country', o?.value || '')
    handle('phone_code', code)
  }
  const onPhoneChange = v => handle('phone', v.replace(/\D/g, ''))

  const handleFile = e => {
    const f = e.target.files?.[0]; if (!f) return
    const img = new Image()
    img.src = URL.createObjectURL(f)
    img.onload = () => {
      const MAX = 560
      let w = img.width, h = img.height
      if (w > MAX || h > MAX) { if (w > h) { h = Math.round(h * MAX / w); w = MAX } else { w = Math.round(w * MAX / h); h = MAX } }
      const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h
      canvas.getContext('2d').drawImage(img, 0, 0, w, h)
      canvas.toBlob(b => {
        const resized = new File([b], f.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' })
        setFile(resized); handle('avatar_url', URL.createObjectURL(resized))
      }, 'image/jpeg', 0.9)
    }
  }

  const srcAvatar = form.avatar_url
    ? /^(blob:|data:|http)/.test(form.avatar_url) ? form.avatar_url : `${API_BASE}${form.avatar_url}`
    : ''

  const fullName = (form.display_name || `${(form.first_name || '').trim()} ${(form.last_name || '').trim()}`.trim()).trim()
  const canSave = !!fullName

  const save = async () => {
    setSaving(true); setErr('')
    try {
      const fd = new FormData()
        ;['first_name', 'last_name', 'display_name', 'job_title', 'phone_code', 'phone', 'country', 'about_me'].forEach(k => fd.append(k, form[k] || ''))
      if (file) fd.append('avatar', file)
      const { data } = await updateMyProfile(fd)
      if (data?.avatar_url) handle('avatar_url', data.avatar_url)
      setFile(null)
    } catch { setErr('บันทึกไม่สำเร็จ') } finally { setSaving(false) }
  }

  return (
    <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="max-w-5xl mx-auto p-6">
      <div className="bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/5 rounded-2xl p-6 md:p-8 mb-6">
        <div className="flex items-start gap-4">
          <label className="w-[112px] h-[112px] md:w-[128px] md:h-[128px] rounded-full border-2 border-white/70 ring-2 ring-primary/20 overflow-hidden grid place-items-center bg-white/60 cursor-pointer">
            {srcAvatar ? <img src={srcAvatar} alt="avatar" className="object-cover w-full h-full" /> : <ImagePlus size={32} className="text-gray-500" />}
            <input type="file" accept="image/*" className="hidden" onChange={handleFile} />
          </label>
          <div className="flex-1 grid gap-3 min-w-0">
            <div className="text-2xl md:text-3xl font-semibold leading-tight break-words">{fullName || 'โปรไฟล์ของฉัน'}</div>
            <div className="flex flex-wrap gap-2">
              {form.job_title ? <span className="px-2 py-1 rounded-full text-xs bg-primary/10 text-primary inline-flex items-center gap-1"><Briefcase size={12} /> {form.job_title}</span> : null}
              {form.email ? <span className="px-2 py-1 rounded-full text-xs bg-secondary/10 text-secondary inline-flex items-center gap-1"><AtSign size={12} /> {form.email}</span> : null}
              {form.country ? <span className="px-2 py-1 rounded-full text-xs bg-gray-900/5 text-gray-800 inline-flex items-center gap-1"><Flag size={12} /> {form.country}</span> : null}
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader className="animate-spin" /></div>
      ) : (
        <div className="grid lg:grid-cols-[320px_1fr] gap-6">
          <div className="bg-white rounded-2xl shadow-md p-5 grid gap-4">
            <div className="text-sm font-semibold mb-1 flex items-center gap-2"><User2 size={16} /> ข้อมูลแสดงผล</div>
            <div className="grid gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium">ชื่อที่แสดง</label>
                <input value={form.display_name || ''} onChange={e => handle('display_name', e.target.value)} placeholder="ชื่อที่แสดง" className="border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium">ชื่อ</label>
                  <input value={form.first_name || ''} onChange={e => handle('first_name', e.target.value)} placeholder="ชื่อ" className="border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary" />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium">สกุล</label>
                  <input value={form.last_name || ''} onChange={e => handle('last_name', e.target.value)} placeholder="สกุล" className="border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary" />
                </div>
              </div>
            </div>
            <div className="text-sm font-semibold mt-3 flex items-center gap-2"><Briefcase size={16} /> งานและตำแหน่ง</div>
            <input value={form.job_title || ''} onChange={e => handle('job_title', e.target.value)} placeholder="ตำแหน่งงาน" className="border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary" />
          </div>

          <div className="bg-white rounded-2xl shadow-md p-5 grid gap-6 overflow-hidden">
            <form onSubmit={e => { e.preventDefault(); if (canSave) save() }}>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1 min-w-0">
                  <label className="text-sm font-medium flex items-center gap-1"><Flag size={14} /> ประเทศ</label>
                  <Select classNamePrefix="rs" options={optCountries} value={optCountries.find(o => o.value === form.country) || optCountries[0]} onChange={onCountryChange} isSearchable />
                </div>
                <div className="flex flex-col gap-1 min-w-0">
                  <label className="text-sm font-medium flex items-center gap-1"><Phone size={14} /> โทรศัพท์</label>
                  <div className="flex gap-2 min-w-0 items-stretch">
                    <input
                      value={form.phone_code || ''}
                      onChange={e => handle('phone_code', e.target.value)}
                      placeholder="+66"
                      className="border border-gray-300 w-4 rounded-xl px-3 py-2.5 text-sm basis-28 shrink-0 text-center focus:ring-2 focus:ring-primary"
                    />
                    <input
                      value={form.phone || ''}
                      onChange={e => onPhoneChange(e.target.value)}
                      placeholder="หมายเลข"
                      className="border border-gray-300 rounded-xl px-3 py-2.5 text-sm flex-1 min-w-0 focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
                <div className="md:col-span-2 flex flex-col gap-1">
                  <label className="text-sm font-medium flex items-center gap-1"><AlignLeft size={14} /> เกี่ยวกับฉัน</label>
                  <textarea rows={5} value={form.about_me || ''} onChange={e => handle('about_me', e.target.value)} className="border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary resize-y" />
                </div>
              </div>

              {err && <div className="text-red-600 text-sm">{err}</div>}

              <div className="flex items-center justify-end gap-2 mt-2">
                <button type="button" onClick={init} disabled={saving} className="px-4 py-2.5 rounded-xl border border-gray-300 text-sm">รีเฟรช</button>
                <button type="submit" disabled={saving || !canSave} className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white rounded-xl px-4 py-2.5 text-sm disabled:opacity-50">
                  {saving ? <Loader className="animate-spin" size={16} /> : <Save size={16} />}
                  บันทึก
                </button>
              </div>
            </form>
          </div>

          <div></div>
          <div className="bg-white rounded-2xl shadow-md p-5">
            <div className="text-sm font-semibold flex items-center gap-2 mb-2">
              <Link size={16} />
              เชื่อมต่อโซเซียลมิเดีย
            </div>
            <div className="text-sm flex items-center gap-2 mb-2">
              เชื่อมต่อบัญชีโซเชียลมิเดียของคุณเพื่อการเข้าถึงที่สะดวกยิ่งขึ้น
            </div>

            <div className="flex items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <img src="icons/google.svg" alt="google" className="w-6 h-6 mt-0.5" />
                <div className="flex flex-col">
                  <span className="text-sm font-medium">Google</span>

                  {connectedGoogle && (
                    <span className="text-xs text-gray-500">
                      {connectSocial?.google_email}
                    </span>
                  )}
                </div>
              </div>

              {connectedGoogle ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-green-600 font-medium">
                    <Check size={12} className="inline-block mr-0.5" />
                    เชื่อมต่อแล้ว
                  </span>
                  <button
                    type="button"
                    className="text-xs px-3 py-1.5 rounded-lg border border-red-300 text-red-600 hover:bg-red-50"
                    onClick={handleDisconnectGoogle}
                  >
                    ยกเลิก
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => connectGoogle()}
                  className="text-sm px-3 py-2 rounded-xl border border-gray-300 hover:bg-gray-50" >
                  เชื่อมต่อ
                </button>
              )}
            </div>
          </div>
        </div>
      )}


      {delItem && (
        <ConfirmModal
          type="confirm"
          title="ยืนยันการยกเลิกการเชื่อมต่อ"
          message={`ต้องการยกเลิกการเชื่อมต่อ Google ใช่หรือไม่?`}
          pending={deleting}
          onConfirm={confirmDelete}
          onCancel={() => setDelItem(false)}
        />
      )}

      {alert.open && <ConfirmModal type="alert" title="แจ้งเตือน" message={alert.msg} onClose={() => setAlert({ open: false, msg: '' })} />}
    </motion.div>
  )
}
